import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, FileDown, Search, ShieldCheck, WifiOff, X } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ROLE_LABELS } from "@/constants/workflow";

const PAGE_SIZE = 50;
const EMPTY = { project_id: "", user_id: "", role: "", module: "", action: "", date_from: "", date_to: "", q: "" };

const label = (value) => (value == null || value === "" ? "—" : String(value));
const stamp = (iso) => (iso ? iso.replace("T", " ").slice(0, 19) : "—");

const Select = ({ testid, value, onChange, children }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="mono h-8 rounded-md border border-slate-200 bg-[#F8FAFC] px-2 text-[10px] uppercase tracking-wider text-slate-600"
  >
    {children}
  </select>
);

const Json = ({ value }) => {
  if (value == null || (typeof value === "object" && Object.keys(value).length === 0))
    return <p className="mono text-[11px] text-slate-400">—</p>;
  return (
    <pre className="mono max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-2.5 text-[10.5px] leading-relaxed text-slate-700">
      {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
};

/** Admin-only, append-only record of every meaningful action across all projects. */
export function AuditLogSection() {
  const [filters, setFilters] = useState(EMPTY);
  const [options, setOptions] = useState({ users: [], projects: [], modules: [], actions: [], roles: [] });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  const params = useMemo(() => {
    const out = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value) out[key] = value;
    });
    return out;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/audit", {
        params: { ...params, page, page_size: PAGE_SIZE },
      });
      setRows(data.items);
      setTotal(data.total);
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setLoading(false);
    }
  }, [params, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get("/admin/audit/filters")
      .then(({ data }) => setOptions(data))
      .catch(() => {});
  }, []);

  const set = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const exportUrl = (kind) => {
    const query = new URLSearchParams(params).toString();
    return `${API}/admin/audit/export.${kind}${query ? `?${query}` : ""}`;
  };

  const pages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  return (
    <div data-testid="audit-log-section" className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mono flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
            <ShieldCheck size={13} className="text-cyan-600" /> Append-only
          </span>
          <Select testid="audit-filter-project" value={filters.project_id} onChange={(v) => set("project_id", v)}>
            <option value="">All projects</option>
            {options.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code || p.name || p.id}
              </option>
            ))}
          </Select>
          <Select testid="audit-filter-user" value={filters.user_id} onChange={(v) => set("user_id", v)}>
            <option value="">All users</option>
            {options.users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
              </option>
            ))}
          </Select>
          <Select testid="audit-filter-role" value={filters.role} onChange={(v) => set("role", v)}>
            <option value="">All roles</option>
            {options.roles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r] || r}
              </option>
            ))}
          </Select>
          <Select testid="audit-filter-module" value={filters.module} onChange={(v) => set("module", v)}>
            <option value="">All modules</option>
            {options.modules.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
          <Select testid="audit-filter-action" value={filters.action} onChange={(v) => set("action", v)}>
            <option value="">All actions</option>
            {options.actions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>
          <input
            data-testid="audit-filter-from"
            type="date"
            value={filters.date_from}
            onChange={(e) => set("date_from", e.target.value)}
            className="mono h-8 rounded-md border border-slate-200 bg-[#F8FAFC] px-2 text-[10px] text-slate-600"
          />
          <input
            data-testid="audit-filter-to"
            type="date"
            value={filters.date_to}
            onChange={(e) => set("date_to", e.target.value)}
            className="mono h-8 rounded-md border border-slate-200 bg-[#F8FAFC] px-2 text-[10px] text-slate-600"
          />
          <div className="flex items-center gap-1">
            <Input
              data-testid="audit-search-input"
              value={search}
              placeholder="Search user, item, reason…"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && set("q", search)}
              className="mono h-8 w-52 border-slate-200 bg-[#F8FAFC] text-[11px]"
            />
            <Button
              data-testid="audit-search-button"
              onClick={() => set("q", search)}
              className="h-8 rounded-full bg-cyan-500 px-3 text-[10px] text-[#04121a]"
            >
              <Search size={12} />
            </Button>
          </div>
          <Button
            data-testid="audit-reset-button"
            variant="outline"
            onClick={() => {
              setSearch("");
              setFilters(EMPTY);
              setPage(1);
            }}
            className="mono h-8 rounded-full border-slate-200 px-3 text-[10px] uppercase tracking-wider"
          >
            Reset
          </Button>
          <div className="ml-auto flex items-center gap-1.5">
            <a data-testid="audit-export-xlsx" href={exportUrl("xlsx")}>
              <Button variant="outline" className="mono h-8 rounded-full border-slate-200 px-3 text-[10px] uppercase tracking-wider">
                <FileDown size={12} className="mr-1" /> Excel
              </Button>
            </a>
            <a data-testid="audit-export-pdf" href={exportUrl("pdf")}>
              <Button variant="outline" className="mono h-8 rounded-full border-slate-200 px-3 text-[10px] uppercase tracking-wider">
                <FileDown size={12} className="mr-1" /> PDF
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="okn-scroll overflow-x-auto">
          <table className="w-full min-w-[1050px] text-left">
            <thead className="mono bg-slate-50 text-[9.5px] uppercase tracking-wider text-slate-500">
              <tr>
                {["Time", "Project", "User", "Role", "Module", "Action", "Item", "Source"].map((head) => (
                  <th key={head} className="px-3 py-2.5 font-normal">
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-10 text-center text-xs text-slate-500">
                    Loading audit entries…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td data-testid="audit-empty" colSpan={8} className="px-3 py-10 text-center text-xs text-slate-500">
                    No audit entries match these filters.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    data-testid={`audit-row-${row.id}`}
                    onClick={() => setSelected(row)}
                    className="cursor-pointer border-t border-slate-100 text-[11.5px] text-slate-700 transition-colors hover:bg-cyan-50/50"
                  >
                    <td className="mono whitespace-nowrap px-3 py-2 text-[10.5px]">{stamp(row.at)}</td>
                    <td className="mono px-3 py-2 text-[10.5px]">{label(row.project_code)}</td>
                    <td className="px-3 py-2">
                      <span className="block truncate">{label(row.user_name)}</span>
                      <span className="mono block truncate text-[9.5px] text-slate-400">{label(row.user_email)}</span>
                    </td>
                    <td className="mono px-3 py-2 text-[10px] uppercase tracking-wider">
                      {ROLE_LABELS[row.user_role] || label(row.user_role)}
                    </td>
                    <td className="mono px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500">
                      {label(row.module)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`mono rounded-full px-2 py-0.5 text-[9.5px] uppercase tracking-wider ${
                          row.action?.includes("delete") || row.action === "access_denied" || row.action === "login_failed"
                            ? "bg-red-50 text-red-600"
                            : row.action?.includes("create")
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {label(row.action)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="block max-w-[260px] truncate">{label(row.target_name)}</span>
                      <span className="mono block text-[9.5px] text-slate-400">{label(row.target_type)}</span>
                    </td>
                    <td className="mono px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500">
                      {row.source === "offline_sync" ? (
                        <span className="flex items-center gap-1 text-amber-600">
                          <WifiOff size={11} /> offline
                        </span>
                      ) : (
                        "online"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2">
          <span data-testid="audit-total" className="mono text-[10px] uppercase tracking-wider text-slate-500">
            {total} entries · page {page} / {pages}
          </span>
          <div className="flex items-center gap-1">
            <Button
              data-testid="audit-prev-page"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              className="h-7 rounded-full border-slate-200 px-2"
            >
              <ChevronLeft size={13} />
            </Button>
            <Button
              data-testid="audit-next-page"
              variant="outline"
              disabled={page >= pages}
              onClick={() => setPage((p) => Math.min(p + 1, pages))}
              className="h-7 rounded-full border-slate-200 px-2"
            >
              <ChevronRight size={13} />
            </Button>
          </div>
        </div>
      </div>

      {selected ? (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={() => setSelected(null)}>
          <aside
            data-testid="audit-detail-drawer"
            onClick={(e) => e.stopPropagation()}
            className="okn-scroll h-full w-full max-w-lg overflow-y-auto bg-white p-5 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="mono text-[10px] uppercase tracking-wider text-slate-500">{selected.module}</p>
                <h3 className="font-display text-lg font-semibold">{label(selected.action)}</h3>
              </div>
              <button data-testid="audit-detail-close" onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700">
                <X size={18} />
              </button>
            </div>

            <dl className="mt-4 grid grid-cols-1 gap-3 text-[11.5px] sm:grid-cols-2">
              {[
                ["Server time", stamp(selected.at)],
                ["Device time", stamp(selected.client_at)],
                ["Synced at", stamp(selected.synced_at)],
                ["Source", selected.source],
                ["Project", `${label(selected.project_code)} ${selected.project_name ? `· ${selected.project_name}` : ""}`],
                ["User", label(selected.user_name)],
                ["Email", label(selected.user_email)],
                ["User ID", label(selected.user_id)],
                ["Active role", ROLE_LABELS[selected.user_role] || label(selected.user_role)],
                ["Item type", label(selected.target_type)],
                ["Item", label(selected.target_name)],
                ["Item ID", label(selected.target_id)],
                ["Reason", label(selected.reason)],
                ["IP", label(selected.ip)],
              ].map(([key, value]) => (
                <div key={key}>
                  <dt className="mono text-[9.5px] uppercase tracking-wider text-slate-400">{key}</dt>
                  <dd className="break-words text-slate-700">{value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-5 space-y-3">
              <div>
                <p className="mono mb-1 text-[9.5px] uppercase tracking-wider text-slate-400">Previous value</p>
                <Json value={selected.before} />
              </div>
              <div>
                <p className="mono mb-1 text-[9.5px] uppercase tracking-wider text-slate-400">New value</p>
                <Json value={selected.after} />
              </div>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
